import { PencilLine, PlusCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';

import { CaseReportReviewerCard } from './CaseReportReviewerCard';
import { CaseReportStatusStrip } from './CaseReportStatusStrip';

interface CaseReportLike {
  id: string;
  status: 'draft' | 'submitted' | 'approved' | 'rejected';
  authorId?: string | null;
  reviewerId?: string | null;
  approvedById?: string | null;
  approvalDate?: string | null;
  rejectionReason?: string | null;
  createdAt: string;
  updatedAt?: string | null;
}

interface Props {
  report: CaseReportLike;
  showReviewer: boolean;
  /** Current user id — used to disambiguate "you're the reviewer" vs
   *  "awaiting another reviewer" without needing a name lookup. */
  currentUserId: string | null;
  onApprove: () => void;
  onReject: (reason: string) => void;
  isApproving: boolean;
  isRejecting: boolean;
}

const formatRelative = (iso: string | null | undefined): string => {
  if (iso === null || iso === undefined) return '—';
  return new Date(iso).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

function ActivityCard({ report }: { report: CaseReportLike }) {
  // Two stacked rows with a subtle hairline between them — each row pairs a
  // small icon-led label with a chip-shaped date so the dates feel like
  // discrete data tokens rather than wrapping body text. The mint chip on
  // the most-recent edit makes "what just happened" scannable at a glance.
  const rows = [
    {
      key: 'created',
      label: 'Created',
      Icon: PlusCircle,
      iso: report.createdAt,
      tone: 'neutral' as const,
    },
    {
      key: 'edited',
      label: 'Last edited',
      Icon: PencilLine,
      iso: report.updatedAt ?? report.createdAt,
      tone: 'mint' as const,
    },
  ];

  return (
    <section className="border-accent-ink/8 rounded-2xl border bg-white p-5 shadow-xs">
      <h3 className="font-display tracking-eyebrow text-accent-ink text-[11px] font-medium uppercase italic">
        Activity
      </h3>
      <ul className="divide-accent-ink/8 mt-3 divide-y">
        {rows.map(({ key, label, Icon, iso, tone }) => (
          <li
            key={key}
            className="flex items-center justify-between gap-3 py-2.5 first:pt-1 last:pb-0"
          >
            <span className="text-accent-ink/80 inline-flex items-center gap-2 text-[12.5px]">
              <Icon className="text-accent-ink/40 h-3.5 w-3.5" strokeWidth={1.5} aria-hidden />
              {label}
            </span>
            <span
              className={cn(
                'inline-flex items-center rounded-full px-2.5 py-0.5 font-mono text-[11px] tabular-nums ring-1',
                tone === 'mint'
                  ? 'bg-primary-50 text-primary-700 ring-primary-200/60'
                  : 'text-accent-ink/85 ring-accent-ink/10 bg-neutral-50',
              )}
            >
              {formatRelative(iso)}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function RejectionNotes({ report }: { report: CaseReportLike }): ReactNode {
  const reason = report.rejectionReason ?? '';
  if (report.status !== 'rejected' || reason === '') {
    return null;
  }
  return (
    <section className="border-warning-300/70 bg-warning-50/80 rounded-2xl border p-5 shadow-xs">
      <h3 className="font-display tracking-eyebrow text-warning-700 text-[11px] font-medium uppercase italic">
        Reviewer notes
      </h3>
      <p className="text-accent-ink mt-2 text-[13.5px] leading-relaxed">{reason}</p>
    </section>
  );
}

/**
 * Editorial helper line under the status strip. Three branches:
 *   - submitted + you're the assigned reviewer   → "Action required"
 *   - submitted + somebody else is the reviewer  → "Awaiting review"
 *   - approved + approvedById is null             → "Auto-published"
 *     (volunteer reports — BE explicitly leaves
 *     approvedById null to mark this branch)
 *   - approved + approvedById === authorId        → "Auto-approved"
 *     (no lead/chief in chain at submit time)
 *   - approved (normal)                          → no extra line
 *   - rejected                                   → handled by RejectionNotes below
 *
 * No reviewer NAME is shown — the case-report response only carries the
 * reviewer's ID, and resolving names requires a user-lookup endpoint
 * we don't have. Generic copy is honest about what we know.
 */
function ReviewContextLine({
  report,
  currentUserId,
}: {
  report: CaseReportLike;
  currentUserId: string | null;
}): ReactNode {
  // Shared chip recipe — tighter padding, taller line-height, soft-glow dot
  // so the chips read as status banners rather than tag pills.
  const chipBase =
    'mt-4 inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-medium ring-1';
  const dotBase = 'h-1.5 w-1.5 rounded-full';

  if (report.status === 'submitted') {
    const isMine = currentUserId !== null && report.reviewerId === currentUserId;
    if (isMine) {
      return (
        <p
          className={`${chipBase} bg-primary-50 text-primary-700 ring-primary-200/70 shadow-primary-700/5 shadow-sm`}
        >
          <span
            aria-hidden
            className={`${dotBase} bg-primary-500 shadow-[0_0_0_3px_rgba(2,97,74,0.15)]`}
          />
          Action required — you&rsquo;re the reviewer
        </p>
      );
    }
    return (
      <p className={`${chipBase} text-accent-ink bg-accent-ink/8 ring-accent-ink/15`}>
        <span aria-hidden className={`${dotBase} bg-accent-ink/60`} />
        Awaiting review
      </p>
    );
  }
  if (report.status === 'approved') {
    if (report.approvedById === null || report.approvedById === undefined) {
      return (
        <p className={`${chipBase} bg-neutral-100 text-neutral-600 ring-neutral-200/70`}>
          <span aria-hidden className={`${dotBase} bg-neutral-400`} />
          Auto-published — no review step
        </p>
      );
    }
    if (report.approvedById === report.authorId) {
      return (
        <p className={`${chipBase} text-warning-700 bg-warning-50 ring-warning-300/60`}>
          <span aria-hidden className={`${dotBase} bg-warning-500`} />
          Auto-approved — no reviewer in chain
        </p>
      );
    }
  }
  return null;
}

export function CaseReportSidePanel({
  report,
  showReviewer,
  currentUserId,
  onApprove,
  onReject,
  isApproving,
  isRejecting,
}: Props) {
  return (
    <div className="flex flex-col gap-3">
      <section className="border-accent-ink/8 rounded-2xl border bg-white p-5 shadow-xs">
        <h3 className="font-display tracking-eyebrow text-accent-ink text-[11px] font-medium uppercase italic">
          Status
        </h3>
        <div className="mt-4">
          <CaseReportStatusStrip report={report} />
        </div>
        <ReviewContextLine report={report} currentUserId={currentUserId} />
      </section>
      <ActivityCard report={report} />
      {showReviewer ? (
        <CaseReportReviewerCard
          onApprove={onApprove}
          onReject={onReject}
          isApproving={isApproving}
          isRejecting={isRejecting}
        />
      ) : null}
      <RejectionNotes report={report} />
    </div>
  );
}
